import { EventEmitter } from 'eventemitter3';
import _ from 'lodash';
import { getPaymentStatus } from './dgs';
import {
  PAYMENT_STATUS,
  isStatusSuccess,
  isStatusPending,
  isStatusCanceled,
} from 'shared/constants/payment-statuses';

const CASHIER_HOST = '/';

export const NO_PAYMENT_METHODS = 'no_payment_methods';
export const UNKNOWN_ERROR = 'unknown_error';

const PAYMENT_POPUP_OPEN = 'payment:popup:open';
const PAYMENT_POPUP_CLOSE = 'payment:popup:close';

/**
 * @typedef {Object} CashierPaymentsWrapperConfig
 * @property {string} [orderId]
 * @property {string} appDefId
 * @property {string} instanceId
 * @property {string} instance
 * @property {string} locale
 */

export class CashierPaymentsWrapper {
  /**
   * @param {CashierPayments} paymentsSDK
   */
  constructor(paymentsSDK) {
    this.instance = paymentsSDK;
    this.events = new EventEmitter();
    this._popupCloseTimeout = null;
    this.config = {};
  }

  /**
   * Initialize Cashier SDK instance for payment
   * @param {CashierPaymentsWrapperConfig} config
   * @returns {CashierPaymentsWrapper}
   */
  configure({
    orderId,
    appDefId,
    instanceId,
    instance,
    locale,
    msid,
    visitorId,
  }) {
    this.config = { orderId, appDefId, instanceId, instance, locale };
    this.instance.init({
      type: 'RAW',
      host: CASHIER_HOST,
      appId: appDefId,
      snapshotId: orderId,
      appInstanceId: instanceId,
      msid,
      visitorId,
      walletPaymentInNewTab: true,
      noPrefetchOnInit: true,
      query: {
        appDefId,
        instanceId,
        orderId,
        locale,
        submitType: 'external',
      },
      onError: this.handleEvent('onError'),
      onMessage: this.handleEvent('onMessage'),
      onPaymentStart: this.handleEvent('onStart'),
      onPaymentComplete: this.handlePaymentComplete,
      onPaymentMethodChanged: this.handleEvent('onMethodChanged'),
    });

    this.instance.on(PAYMENT_POPUP_OPEN, this.handlePopupOpen);
    this.instance.on(PAYMENT_POPUP_CLOSE, this.handlePopupClose);

    return this;
  }

  /**
   * Initialize Cashier SDK instance for payment
   * @param {CashierPaymentsWrapperConfig} config
   * @return {Promise<{paymentTypes: Array<*>}>}
   */
  init(config) {
    this.configure(config);
    return this.instance.fetchSettings(
      {
        appId: this.config.appDefId,
        appInstanceId: this.config.instanceId,
        snapshotId: this.config.orderId,
      },
      CASHIER_HOST,
    );
  }

  /**
   * Subscribes to SDK events
   * @param {Object<string, function(*)>} handlersMap
   * @return {function()}
   */
  subscribe(handlersMap) {
    const unsubscribers = _.map(handlersMap, (handler, event) => {
      this.events.on(event, handler);
      return () => {
        this.events.off(event, handler);
      };
    });
    return _.flow(unsubscribers);
  }

  /**
   * Get localized error text by code
   * @param {(string|number)} errorCode
   * @param {string} [locale]
   * @returns {Promise<string>}
   */
  localizeError(errorCode, locale) {
    return this.instance.localizeError(errorCode, locale);
  }

  /**
   * @private
   * @param {string} eventName
   * @returns {function(*, *)}
   */
  handleEvent(eventName) {
    return (method, message) => {
      this.events.emit(eventName, { ...message, method });
    };
  }

  /** @private */
  handlePopupOpen = () => {
    clearTimeout(this._popupCloseTimeout);
  };

  /** @private */
  handlePopupClose = () => {
    this._popupCloseTimeout = setTimeout(this.handleStatus, 200, {
      status: PAYMENT_STATUS.CANCEL,
    });
  };

  /** @private */
  handlePaymentComplete = (method, message) => {
    clearTimeout(this._popupCloseTimeout);

    this.instance.off(PAYMENT_POPUP_OPEN, this.handlePopupOpen);
    this.instance.off(PAYMENT_POPUP_CLOSE, this.handlePopupClose);

    return this.checkTransaction({ method, ...message }).then(
      this.handleStatus,
      this.handleStatus,
    );
  };

  /** @private */
  checkTransaction = event => {
    if (event.chargeId) {
      return getPaymentStatus({
        transactionId: event.chargeId,
        orderId: this.config.orderId,
        instance: this.config.instance,
      }).then(
        response => ({ ...event, status: response.status }),
        error => ({ ...event, status: PAYMENT_STATUS.ERROR, error }),
      );
    }
    return Promise.resolve(event);
  };

  /** @private */
  handleStatus = event => {
    if (isStatusSuccess(event.status)) {
      this.events.emit('onSuccess', event);
    } else if (isStatusCanceled(event.status)) {
      this.events.emit('onCancel', event);
    } else if (isStatusPending(event.status)) {
      this.events.emit('onPending', event);
    } else {
      this.events.emit('onFail', event);
    }
  };
}

/**
 * Loads and wraps Cashier SDK
 * @return {Promise<CashierPaymentsWrapper>}
 */
export const getPaymentsSDK = _.memoize(() =>
  import(/* webpackChunkName: 'cashier-payments-sdk'*/ '@wix/cashier-payments-sdk')
    .then(module => module.default || module)
    .then(paymentsSDK => new CashierPaymentsWrapper(paymentsSDK)),
);

/**
 * Loads, configures and fetches payment methods
 * @param {CashierPaymentsWrapperConfig} config
 * @returns {Promise<{paymentTypes: Array<*>}>}
 */
export const configureSDK = config =>
  getPaymentsSDK().then(sdk => sdk.init(config));
